'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuoteFormData {
  // Step 1: Service Selection
  serviceType: string;
  propertyType: string;
  patioSize: string;
  
  // Step 2: Property Details
  address: string;
  city: string;
  zipCode: string;
  additionalServices: string[];
  specialRequirements: string;
  
  // Step 3: Contact & Scheduling
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredDate: string;
  preferredTime: string;
  contactMethod: string;
  
  // Step 4: Additional Information
  hearAboutUs: string;
  urgency: string;
  budget: string;
  additionalNotes: string;
}

interface QuoteFormStepsProps {
  onSubmit: (data: QuoteFormData) => void;
  isSubmitting?: boolean;
}

const QuoteFormSteps = ({ onSubmit, isSubmitting = false }: QuoteFormStepsProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<QuoteFormData>({
    serviceType: '',
    propertyType: '',
    patioSize: '',
    address: '',
    city: '',
    zipCode: '',
    additionalServices: [],
    specialRequirements: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    contactMethod: 'email',
    hearAboutUs: '',
    urgency: 'standard',
    budget: '',
    additionalNotes: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof QuoteFormData, string>>>({});

  const serviceTypes = [
    { value: 'patio-cleaning', label: 'Patio Cleaning', icon: 'HomeIcon' },
    { value: 'pool-deck', label: 'Pool Deck Cleaning', icon: 'BeakerIcon' },
    { value: 'driveway', label: 'Driveway Cleaning', icon: 'TruckIcon' },
    { value: 'furniture', label: 'Outdoor Furniture', icon: 'CubeIcon' },
    { value: 'full-property', label: 'Full Property Service', icon: 'BuildingOfficeIcon' },
  ];

  const propertyTypes = [
    { value: 'residential', label: 'Residential Home' },
    { value: 'condo', label: 'Condo/Townhome' },
    { value: 'commercial', label: 'Commercial Property' },
    { value: 'hoa', label: 'HOA/Community' },
  ];

  const patioSizes = [
    { value: 'small', label: 'Small (< 200 sq ft)', estimate: '$150-250' },
    { value: 'medium', label: 'Medium (200-500 sq ft)', estimate: '$250-400' },
    { value: 'large', label: 'Large (500-1000 sq ft)', estimate: '$400-650' },
    { value: 'xlarge', label: 'Extra Large (> 1000 sq ft)', estimate: '$650+' },
  ];

  const additionalServiceOptions = [
    { value: 'sealing', label: 'Paver Sealing' },
    { value: 'stain-removal', label: 'Deep Stain Removal' },
    { value: 'furniture-cleaning', label: 'Furniture Cleaning' },
    { value: 'gutter-cleaning', label: 'Gutter Cleaning' },
    { value: 'window-cleaning', label: 'Window Cleaning' },
  ];

  const phoenixCities = [
    'Phoenix', 'Scottsdale', 'Tempe', 'Mesa', 'Chandler', 'Gilbert',
    'Glendale', 'Peoria', 'Surprise', 'Avondale', 'Goodyear', 'Buckeye',
    'Queen Creek', 'Fountain Hills', 'Paradise Valley', 'Cave Creek',
  ];

  const timeSlots = [
    '8:00 AM - 10:00 AM',
    '10:00 AM - 12:00 PM',
    '12:00 PM - 2:00 PM',
    '2:00 PM - 4:00 PM',
    '4:00 PM - 6:00 PM',
  ];

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof QuoteFormData, string>> = {};

    if (step === 1) {
      if (!formData.serviceType) newErrors.serviceType = 'Please select a service type';
      if (!formData.propertyType) newErrors.propertyType = 'Please select a property type';
      if (!formData.patioSize) newErrors.patioSize = 'Please select a patio size';
    }

    if (step === 2) {
      if (!formData.address.trim()) newErrors.address = 'Address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
      else if (!/^\d{5}$/.test(formData.zipCode)) newErrors.zipCode = 'Invalid ZIP code format';
    }

    if (step === 3) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
      else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Invalid phone number';
      if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
      if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof QuoteFormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const toggleAdditionalService = (service: string) => {
    const current = formData.additionalServices;
    const updated = current.includes(service)
      ? current.filter(s => s !== service)
      : [...current, service];
    handleInputChange('additionalServices', updated);
  };

  const getEstimatedPrice = () => {
    const sizeOption = patioSizes.find(s => s.value === formData.patioSize);
    return sizeOption?.estimate || 'Contact for quote';
  };

  return (
    <div className="w-full">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full font-headline font-semibold transition-brand ${
                currentStep >= step
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {step}
              </div>
              {step < 4 && (
                <div className={`flex-1 h-1 mx-2 transition-brand ${
                  currentStep > step ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-sm font-body">
          <span className={currentStep >= 1 ? 'text-primary font-medium' : 'text-muted-foreground'}>Service</span>
          <span className={currentStep >= 2 ? 'text-primary font-medium' : 'text-muted-foreground'}>Property</span>
          <span className={currentStep >= 3 ? 'text-primary font-medium' : 'text-muted-foreground'}>Contact</span>
          <span className={currentStep >= 4 ? 'text-primary font-medium' : 'text-muted-foreground'}>Details</span>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Step 1: Service Selection */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Select Service Type *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {serviceTypes.map((service) => (
                  <button
                    key={service.value}
                    type="button"
                    onClick={() => handleInputChange('serviceType', service.value)}
                    className={`p-4 rounded-lg border-2 transition-brand text-left ${
                      formData.serviceType === service.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name={service.icon as any} size={32} className="text-primary mb-2" />
                    <div className="font-body font-medium text-foreground">{service.label}</div>
                  </button>
                ))}
              </div>
              {errors.serviceType && (
                <p className="mt-2 text-sm text-error">{errors.serviceType}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Property Type *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {propertyTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('propertyType', type.value)}
                    className={`p-4 rounded-lg border-2 transition-brand text-left ${
                      formData.propertyType === type.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-body font-medium text-foreground">{type.label}</div>
                  </button>
                ))}
              </div>
              {errors.propertyType && (
                <p className="mt-2 text-sm text-error">{errors.propertyType}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Approximate Size *
              </label>
              <div className="space-y-3">
                {patioSizes.map((size) => (
                  <button
                    key={size.value}
                    type="button"
                    onClick={() => handleInputChange('patioSize', size.value)}
                    className={`w-full p-4 rounded-lg border-2 transition-brand text-left ${
                      formData.patioSize === size.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-body font-medium text-foreground">{size.label}</span>
                      <span className="text-sm text-primary font-semibold">{size.estimate}</span>
                    </div>
                  </button>
                ))}
              </div>
              {errors.patioSize && (
                <p className="mt-2 text-sm text-error">{errors.patioSize}</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Property Details */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div>
              <label htmlFor="address" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Street Address *
              </label>
              <input
                type="text"
                id="address"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="123 Desert View Drive"
              />
              {errors.address && (
                <p className="mt-2 text-sm text-error">{errors.address}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="city" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  City *
                </label>
                <select
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select City</option>
                  {phoenixCities.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                {errors.city && (
                  <p className="mt-2 text-sm text-error">{errors.city}</p>
                )}
              </div>

              <div>
                <label htmlFor="zipCode" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  ZIP Code *
                </label>
                <input
                  type="text"
                  id="zipCode"
                  value={formData.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="85001"
                  maxLength={5}
                />
                {errors.zipCode && (
                  <p className="mt-2 text-sm text-error">{errors.zipCode}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Additional Services (Optional)
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {additionalServiceOptions.map((service) => (
                  <button
                    key={service.value}
                    type="button"
                    onClick={() => toggleAdditionalService(service.value)}
                    className={`p-3 rounded-lg border-2 transition-brand text-left flex items-center space-x-3 ${
                      formData.additionalServices.includes(service.value)
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.additionalServices.includes(service.value)
                        ? 'border-primary bg-primary' :'border-border'
                    }`}>
                      {formData.additionalServices.includes(service.value) && (
                        <Icon name="CheckIcon" size={16} className="text-white" />
                      )}
                    </div>
                    <span className="font-body text-foreground">{service.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="specialRequirements" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Special Requirements or Concerns (Optional)
              </label>
              <textarea
                id="specialRequirements"
                value={formData.specialRequirements}
                onChange={(e) => handleInputChange('specialRequirements', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Describe any specific stains, delicate surfaces, or special considerations..."
              />
            </div>
          </div>
        )}

        {/* Step 3: Contact & Scheduling */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  First Name *
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="John"
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm text-error">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Last Name *
                </label>
                <input
                  type="text"
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Smith"
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm text-error">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="john.smith@example.com"
              />
              {errors.email && (
                <p className="mt-2 text-sm text-error">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="(602) 555-0123"
              />
              {errors.phone && (
                <p className="mt-2 text-sm text-error">{errors.phone}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="preferredDate" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="preferredDate"
                  value={formData.preferredDate}
                  onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {errors.preferredDate && (
                  <p className="mt-2 text-sm text-error">{errors.preferredDate}</p>
                )}
              </div>

              <div>
                <label htmlFor="preferredTime" className="block text-sm font-headline font-semibold text-foreground mb-2">
                  Preferred Time *
                </label>
                <select
                  id="preferredTime"
                  value={formData.preferredTime}
                  onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select Time</option>
                  {timeSlots.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
                {errors.preferredTime && (
                  <p className="mt-2 text-sm text-error">{errors.preferredTime}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Preferred Contact Method *
              </label>
              <div className="flex flex-wrap gap-4">
                {['email', 'phone', 'text'].map((method) => (
                  <button
                    key={method}
                    type="button"
                    onClick={() => handleInputChange('contactMethod', method)}
                    className={`px-6 py-3 rounded-lg border-2 transition-brand font-body font-medium capitalize ${
                      formData.contactMethod === method
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border text-foreground hover:border-primary/50'
                    }`}
                  >
                    {method}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Additional Information */}
        {currentStep === 4 && (
          <div className="space-y-6">
            <div>
              <label htmlFor="hearAboutUs" className="block text-sm font-headline font-semibold text-foreground mb-2">
                How did you hear about us? (Optional)
              </label>
              <select
                id="hearAboutUs"
                value={formData.hearAboutUs}
                onChange={(e) => handleInputChange('hearAboutUs', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select an option</option>
                <option value="google">Google Search</option>
                <option value="referral">Friend/Neighbor Referral</option>
                <option value="social">Social Media</option>
                <option value="nextdoor">Nextdoor</option>
                <option value="yard-sign">Yard Sign</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-headline font-semibold text-foreground mb-3">
                Service Urgency
              </label>
              <div className="space-y-3">
                {[
                  { value: 'urgent', label: 'Urgent (Within 3 days)', desc: 'Need service as soon as possible' },
                  { value: 'standard', label: 'Standard (Within 1-2 weeks)', desc: 'Flexible scheduling' },
                  { value: 'flexible', label: 'Flexible (Anytime)', desc: 'Looking for best available rate' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('urgency', option.value)}
                    className={`w-full p-4 rounded-lg border-2 transition-brand text-left ${
                      formData.urgency === option.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <div className="font-body font-medium text-foreground">{option.label}</div>
                    <div className="text-sm text-muted-foreground mt-1">{option.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="budget" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Budget Range (Optional)
              </label>
              <select
                id="budget"
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select budget range</option>
                <option value="under-200">Under $200</option>
                <option value="200-400">$200 - $400</option>
                <option value="400-600">$400 - $600</option>
                <option value="600-1000">$600 - $1,000</option>
                <option value="over-1000">Over $1,000</option>
              </select>
            </div>

            <div>
              <label htmlFor="additionalNotes" className="block text-sm font-headline font-semibold text-foreground mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                id="additionalNotes"
                value={formData.additionalNotes}
                onChange={(e) => handleInputChange('additionalNotes', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Any other information you'd like us to know..."
              />
            </div>

            {/* Estimated Quote Display */}
            {formData.patioSize && (
              <div className="p-6 rounded-lg bg-primary/5 border-2 border-primary">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-body text-muted-foreground mb-1">Estimated Quote Range</div>
                    <div className="text-2xl font-headline font-bold text-primary">{getEstimatedPrice()}</div>
                  </div>
                  <Icon name="CurrencyDollarIcon" size={48} className="text-primary opacity-20" />
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  Final quote will be provided after property assessment
                </p>
              </div>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="px-6 py-3 rounded-lg border-2 border-border text-foreground font-body font-medium hover:border-primary transition-brand flex items-center space-x-2"
            >
              <Icon name="ChevronLeftIcon" size={20} />
              <span>Previous</span>
            </button>
          )}

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="ml-auto px-6 py-3 rounded-lg bg-primary text-primary-foreground font-body font-medium hover:bg-primary/90 transition-brand flex items-center space-x-2"
            >
              <span>Next Step</span>
              <Icon name="ChevronRightIcon" size={20} />
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting}
              className="ml-auto px-8 py-3 rounded-lg bg-accent text-accent-foreground font-body font-semibold hover:bg-accent/90 transition-brand shadow-cta-glow flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-accent-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Submit Quote Request</span>
                  <Icon name="PaperAirplaneIcon" size={20} />
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default QuoteFormSteps;